// ============================================================================
// JARVISTRAVEL - SECOND-FACTOR PANEL (JAR-1537)
//
// The code form for useSecondFactor. It owns no state and makes no Clerk call:
// everything it shows and does comes from the hook, so whichever screen mounts it
// gets the same behaviour and the same single attemptSecondFactor call site.
//
// Rendered INSTEAD of the screen's own form, never alongside it. By the time a
// second factor is asked for the credentials are already accepted, and
// re-offering them invites a second attempt that lands right back here.
//
// JAR-2872: the texted code joins the panel. It behaves like the emailed Device
// Trust code — Clerk has to SEND it, so it carries the same hint, resend and
// cooldown machinery — and the switch at the bottom offers it beside the
// authenticator (Brent's "other options" screen). The switch SAYS text message
// whenever the attempt carries a phone factor, and SENDS on the first hop onto
// it (togglePhoneCode's job), so a code goes out when the person asks for one
// and not because the panel listed the option.
// ============================================================================

import { KeyRound, ShieldCheck, Smartphone } from 'lucide-react';
import { Button, Input, OfflineNotice } from '../shared';
import type { SecondFactor } from './useSecondFactor';

interface SecondFactorPanelProps {
  secondFactor: SecondFactor;
  /** The way out: back to a sign-in form. Disabled while a code is verifying. */
  onBack: () => void;
  /**
   * The sentence before the authenticator prompt. Defaults to the sign-in
   * wording, which says the password was accepted: without it, a code prompt
   * straight after a correct password reads as a rejection. The reset screen
   * passes an empty string and says the same thing as a status instead, because
   * there the password was not accepted, it was just SET.
   */
  passwordNote?: string;
  /** Announced as a status above the form, for a screen that needs to say what
   *  just succeeded before asking for the code. */
  statusNotice?: string;
  backLabel?: string;
}

function withPasswordNote(passwordNote: string, sentence: string): string {
  return [passwordNote, sentence].filter(Boolean).join(' ');
}

// Device Trust's check on a new device, for an account with no second factor
// (JAR-1708). Worded for what the person has to do, not for which Clerk policy
// asked, and it names the masked address so they know which inbox to open. It
// says a code was sent only once one was.
function emailSentence(secondFactor: SecondFactor): string {
  const address = secondFactor.sentHint || 'your email address';
  switch (secondFactor.sendState) {
    case 'sending':
      return 'Sending a code to your email.';
    case 'sent':
      return `We sent a 6-digit code to ${address}. Enter it to finish signing in.`;
    default:
      return `We couldn't send a code to ${address}. Use Resend code to try again.`;
  }
}

// The enrolled phone factor (JAR-2872). Same machinery as the emailed code, so
// the same three states; the words name the phone, so the person knows which
// device to pick up. It says a code was sent only once one was.
function phoneSentence(secondFactor: SecondFactor): string {
  const phone = secondFactor.sentHint || 'your phone';
  switch (secondFactor.sendState) {
    case 'sending':
      return 'Sending a code to your phone.';
    case 'sent':
      return `We sent a 6-digit code to ${phone}. Enter it to finish signing in.`;
    default:
      return `We couldn't send a code to ${phone}. Use Resend code to try again.`;
  }
}

function codePrompt(secondFactor: SecondFactor, passwordNote: string): string {
  // Backup overlay first: it rides on top of either base (the authenticator or
  // the texted code), so it is the mode on screen whenever it is switched on.
  if (secondFactor.useBackupCode) return 'Enter one of the backup codes you saved when you set this up.';
  if (secondFactor.viaPhone && secondFactor.viaSentCode) return withPasswordNote(passwordNote, phoneSentence(secondFactor));
  if (secondFactor.viaSentCode) return withPasswordNote(passwordNote, emailSentence(secondFactor));
  return withPasswordNote(passwordNote, 'Enter the 6-digit code from your authenticator app.');
}

function codeLabel(secondFactor: SecondFactor): string {
  if (secondFactor.useBackupCode) return 'Backup code';
  if (secondFactor.viaPhone && secondFactor.viaSentCode) return 'Verification code';
  if (secondFactor.viaSentCode) return 'Email code';
  return 'Authentication code';
}

function heading(secondFactor: SecondFactor): string {
  if (secondFactor.useBackupCode) return 'Two-factor authentication';
  if (secondFactor.viaPhone && secondFactor.viaSentCode) return 'Check your phone';
  if (secondFactor.viaSentCode) return 'Check your email';
  return 'Two-factor authentication';
}

function resendLabel(secondFactor: SecondFactor): string {
  if (secondFactor.isSending) return 'Sending code';
  // Disabled while a code is checked, so it says why rather than offering a resend.
  if (secondFactor.isVerifying) return 'Verifying code';
  if (secondFactor.resendIn > 0) return `Resend code in ${secondFactor.resendIn}s`;
  return 'Resend code';
}

// The bottom-of-panel switch (Brent's "other options", JAR-2872). Shown only
// when the attempt offers a choice; the label names where each hop goes. The
// first hop onto the texted code SENDS it (togglePhoneCode), and every switch is
// inert while a code verifies.
function alternatives(secondFactor: SecondFactor): { label: string; onClick: () => void } | null {
  // Standing on the texted code: the way back is the authenticator, when the
  // account has one. Beside only backup codes, the backup toggle below is the
  // way back, so there is nothing for this switch to offer.
  if (secondFactor.viaPhone && secondFactor.viaSentCode) {
    return secondFactor.options.totp
      ? { label: 'Use your authenticator app instead', onClick: secondFactor.togglePhoneCode }
      : null;
  }
  // Standing on a held factor (the authenticator, or its backup overlay): the
  // text is the alternative at the bottom.
  if (secondFactor.phoneOffered) {
    return { label: 'Use a text message code instead', onClick: secondFactor.togglePhoneCode };
  }
  return null;
}

// Where the backup toggle's way-back label points: where the person came
// from. On a TOTP base that is the authenticator; on the texted-code base it
// is the texted code itself. (web-app#859 round 2: this once read options.totp
// only, so a phone+backup account's way back named a factor it had never had.)
function backupWayBack(secondFactor: SecondFactor): string {
  return secondFactor.viaPhone && secondFactor.viaSentCode
    ? 'Use your texted code instead'
    : 'Use your authenticator app instead';
}

export function SecondFactorPanel({
  secondFactor,
  onBack,
  passwordNote = 'Your password was accepted.',
  statusNotice,
  backLabel = 'Use a different account',
}: SecondFactorPanelProps) {
  const alternative = alternatives(secondFactor);
  return (
    <div className="flex-1 px-6 pb-8">
      <div className="bg-surface border border-border rounded-3xl shadow-xl shadow-black/5 p-6 max-w-md mx-auto">
        <div className="text-center mb-6">
          <div className="w-14 h-14 mx-auto bg-accent/10 rounded-2xl flex items-center justify-center text-accent mb-3">
            <ShieldCheck className="w-7 h-7" strokeWidth={1.75} />
          </div>
          <h2 className="text-lg font-semibold text-body">{heading(secondFactor)}</h2>
          {statusNotice && (
            <p role="status" className="text-sm text-body mt-2">
              {statusNotice}
            </p>
          )}
          <p className="text-muted text-sm mt-1">{codePrompt(secondFactor, passwordNote)}</p>
        </div>

        <form
          className="space-y-5"
          onSubmit={(e) => {
            e.preventDefault();
            // Rejects only when the screen's onComplete throws, with the session
            // already active. Left to App's global unhandledrejection net, which
            // reports it and shows a toast; nothing here could say more.
            void secondFactor.verify();
          }}
        >
          <Input
            type="text"
            label={codeLabel(secondFactor)}
            placeholder={secondFactor.useBackupCode ? 'xxxxxxxx' : '123456'}
            value={secondFactor.code}
            // An authenticator code is six digits. inputMode only shapes a phone's
            // keyboard, so a desktop could type letters into a code that can only
            // fail (web-app#384 review). A backup code keeps what was typed.
            onChange={(e) =>
              secondFactor.setCode(
                secondFactor.useBackupCode ? e.target.value : e.target.value.replace(/\D/g, '').slice(0, 6),
              )
            }
            error={secondFactor.error}
            leftIcon={<KeyRound className="w-5 h-5" strokeWidth={1.75} />}
            autoComplete="one-time-code"
            {...(secondFactor.useBackupCode
              ? {}
              : {
                  // inputMode and the one-time-code hint together are what make a
                  // phone offer the authenticator code rather than the keyboard's
                  // word suggestions.
                  inputMode: 'numeric' as const,
                  // No maxLength: a browser cuts a paste to it BEFORE onChange, so
                  // "123 456" copied from an authenticator would arrive as "123 45".
                  // onChange keeps six digits instead.
                })}
            autoFocus
          />

          <OfflineNotice />

          <Button type="submit" variant="primary" fullWidth loading={secondFactor.isVerifying} size="lg">
            Verify
          </Button>

          {/* An emailed or texted code can go astray or expire, and without a way
              to ask again the only way on is to start the whole sign-in over.
              Inert while one is sending, during the cooldown after a send (the
              same wait the other email-code screens use), or while a code is
              verifying. */}
          {secondFactor.viaSentCode && (
            <button
              type="button"
              className="w-full text-sm text-muted hover:text-body transition-colors"
              onClick={secondFactor.resend}
              disabled={secondFactor.isSending || secondFactor.resendIn > 0 || secondFactor.isVerifying}
            >
              {resendLabel(secondFactor)}
            </button>
          )}

          {/* The fallback toggle, offered only when the account holds BOTH the
              base factor (authenticator, or the texted code of a phone+backup
              account) and backup codes. A toggle to a factor the account lacks
              is a control that cannot work. With the texted code as base the
              way back is the text itself, so the label points there. */}
          {(secondFactor.options.totp || secondFactor.viaPhone) && secondFactor.options.backup && (
            <button
              type="button"
              className="w-full text-sm text-muted hover:text-body transition-colors"
              onClick={secondFactor.toggleBackupCode}
              // Inert while a code verifies, like the way out below: switching
              // mid-flight cleared the field while the first attempt could still land
              // (web-app#384 review).
              disabled={secondFactor.isVerifying}
            >
              {secondFactor.useBackupCode ? backupWayBack(secondFactor) : 'Use a backup code instead'}
            </button>
          )}

          {/* The texted-code alternative (JAR-2872), at the bottom as Brent asked:
              the authenticator stays primary, and this is the way onto SMS. The
              first hop sends the code. Inert while a code verifies, like every
              switch here. */}
          {alternative && (
            <button
              type="button"
              className="w-full text-sm text-muted hover:text-body transition-colors"
              onClick={alternative.onClick}
              disabled={secondFactor.isVerifying}
            >
              <span className="inline-flex items-center justify-center gap-2">
                <Smartphone className="w-4 h-4" strokeWidth={1.75} />
                {alternative.label}
              </span>
            </button>
          )}

          <button
            type="button"
            className="w-full text-sm text-muted hover:text-body transition-colors"
            onClick={onBack}
            // Disabled while Clerk is still answering (web-app#305 review). Backing
            // out only resets the screen; the request in flight would still land,
            // and could sign the user in after they chose a different account.
            disabled={secondFactor.isVerifying}
          >
            {backLabel}
          </button>
        </form>
      </div>
    </div>
  );
}

export default SecondFactorPanel;
